/**
 * Product mega-menu + graphical showcase (Cvent-style IA).
 * Icon keys map to lucide components in FeatureIcon.jsx.
 */

export const PRODUCT_MENU = {
  label: "Products",
  seeAllHref: "#platform",
  seeAllLabel: "See all platform capabilities",
  columns: [
    {
      title: "Plan & promote",
      links: [
        { label: "Registration & ticketing", href: "#platform", icon: "ticket", description: "Branded flows, paid or free" },
        { label: "Event websites", href: "#platform", icon: "layout", description: "Publish sites in hours, not weeks" },
        { label: "Email campaigns", href: "#platform", icon: "mail", description: "Segmented comms with delivery tracking" },
        { label: "Form builder", href: "#platform", icon: "form", description: "Custom fields and approval rules" },
      ],
    },
    {
      title: "Run onsite & hybrid",
      links: [
        { label: "QR check-in & badging", href: "#platform", icon: "qr", description: "Scan, validate, print on arrival" },
        { label: "Wallet passes", href: "#platform", icon: "wallet", description: "Apple & Google Wallet for attendees" },
        { label: "Live gate monitoring", href: "#platform", icon: "scan", description: "Real-time throughput at every entrance" },
        { label: "Hybrid programs", href: "#event-types", icon: "hybrid", description: "One console for in-person + virtual" },
      ],
    },
    {
      title: "Measure & scale",
      links: [
        { label: "Analytics & reporting", href: "#platform", icon: "chart", description: "Registration, attendance, revenue" },
        { label: "API & webhooks", href: "#platform", icon: "api", description: "Sync CRM, finance, and data warehouse" },
        { label: "Team & audit log", href: "#platform", icon: "users", description: "Roles, invites, change history" },
        { label: "Trust Centre", href: "/trust", icon: "shield", description: "SOC 2 programme, PDPA/GDPR, security docs" },
      ],
    },
  ],
  featured: [
    {
      title: "Event operations",
      body: "Run registration through check-in in one console.",
      href: "#platform",
      icon: "operations",
      cta: "Explore platform",
    },
    {
      title: "Event marketing",
      body: "Launch campaigns that match your registration data.",
      href: "#platform",
      icon: "megaphone",
      cta: "See comms tools",
    },
    {
      title: "Studio plan",
      body: "SGD 2,880/year — AI co-planner included.",
      href: "#pricing",
      icon: "pricing",
      cta: "View pricing",
      highlight: true,
    },
    {
      title: "Hybrid delivery",
      body: "Onboarding + CSM support from SGD 6,000/year.",
      href: "/contact?intent=sales",
      icon: "handshake",
      cta: "Talk to sales",
    },
  ],
};

export const PRODUCT_SHOWCASE = {
  overline: "Platform",
  title: "Enterprise-grade operations",
  titleMuted: "without enterprise friction.",
  subtitle:
    "One annual subscription in SGD. Registration, onsite check-in, AI co-planner, and reporting — a full event management platform with transparent pricing and no per-ticket fee.",
  categories: [
    {
      id: "plan",
      title: "Plan & promote",
      color: "lime",
      items: [
        {
          icon: "ticket",
          title: "Registration & ticketing",
          body: "Branded registration, approval workflows, and paid ticketing — no per-ticket platform fee.",
          metric: "↑ conversion",
        },
        {
          icon: "layout",
          title: "Event websites",
          body: "Page builder and custom domains for programs that need to look on-brand, fast.",
          metric: "Hours to live",
        },
        {
          icon: "mail",
          title: "Campaigns & comms",
          body: "Email journeys tied to registration status, check-in, and segments.",
          metric: "100k+ credits/yr",
        },
      ],
    },
    {
      id: "onsite",
      title: "Deliver onsite",
      color: "cyan",
      items: [
        {
          icon: "qr",
          title: "Check-in & badges",
          body: "QR validation, instant badge print, and wallet passes for frictionless arrival.",
          metric: "70% shorter queues",
        },
        {
          icon: "scan",
          title: "Live operations",
          body: "Gate-level visibility when peak arrival hits — not after the fact in a CSV.",
          metric: "600+ / hr throughput",
        },
        {
          icon: "hybrid",
          title: "Hybrid & virtual",
          body: "Coordinate tracks, capacity, and attendee state across formats in one system.",
          metric: "One data model",
        },
      ],
    },
    {
      id: "insights",
      title: "Prove ROI",
      color: "violet",
      items: [
        {
          icon: "chart",
          title: "Executive dashboards",
          body: "Registration funnel, attendance, and revenue — portfolio or per event.",
          metric: "24h post-event reports",
        },
        {
          icon: "api",
          title: "Integrations",
          body: "HubSpot, Stripe, webhooks, and export paths for finance and CRM teams.",
          metric: "Scale tier+",
        },
        {
          icon: "shield",
          title: "Governance",
          body: "Audit logs, team roles, and Singapore hosting for APAC enterprise programs.",
          metric: "99.95% uptime",
        },
      ],
    },
  ],
};
